import { Outlet, Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useNotificationStore } from '../../stores/notificationStore';
import {
  BarChart3,
  Bell,
  BookOpen,
  Briefcase,
  Building2,
  CheckSquare,
  ChevronDown,
  Chrome,
  Clapperboard,
  ClipboardList,
  Code2,
  Coins,
  Compass,
  FileDown,
  FileText,
  GitMerge,
  Images,
  LayoutDashboard,
  Link2,
  LogOut,
  Mail,
  MessageCircle,
  Palette,
  PanelLeft,
  PanelLeftClose,
  Scissors,
  ScrollText,
  Send,
  Settings,
  Shield,
  Sparkles,
  Tags,
  User,
  UserCog,
  Users,
  Wallet,
  Webhook,
} from 'lucide-react';
import ThemeToggle from '../ThemeToggle';
import SandboxTryoutButton from '../SandboxTryoutButton';
import OpenKanbanButton from '../OpenKanbanButton';
import KolMyGlassLogo from '../KolMyGlassLogo';
import AppearanceSettings from '../AppearanceSettings';
import { useDesktopSidebarHidden } from '../../hooks/useDesktopSidebarHidden';
import api from '../../lib/api';
import { portalShellProps } from '../../lib/appearance';
import { portalContentClassName, portalMainClassName } from '../../lib/portalContent';
import {
  PortalNavGroups,
  flatNavFromGroups,
  navLinkBase,
  navLinkInactive,
  type NavGroup,
} from './PortalNav';
import { PortalMobileTopBar } from './PortalMobileTopBar';
import { PortalBottomTabs } from './PortalBottomTabs';
import { PortalUserChip } from './PortalUserChip';

type InboxThreadsPage = {
  threads?: { id: string; unreadCountAgency?: number }[];
  nextCursor?: string | null;
};

const INBOX_UNREAD_UPDATE_EVENT = 'agency-inbox-unread-update';

export default function AgencyLayout() {
  const { logout, user } = useAuthStore();
  const { unreadCount, fetchUnreadCount } = useNotificationStore();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [inboxUnreadCount, setInboxUnreadCount] = useState<number | undefined>(undefined);
  const inboxUnreadByThreadRef = useRef(new Map<string, number>());
  const { isDesktopSidebarHidden, setIsDesktopSidebarHidden } = useDesktopSidebarHidden();

  const agencyOrganizationIds = useMemo(
    () =>
      Array.from(
        new Set(
          (user?.memberships || [])
            .filter((membership) => membership.organization?.type === 'AGENCY')
            .map((membership) => membership.organizationId)
        )
      ),
    [user?.memberships]
  );

  useEffect(() => {
    if (!user) return;
    setInboxUnreadCount(undefined);
    inboxUnreadByThreadRef.current = new Map();
    fetchUnreadCount();
    let active = true;
    let inboxFetchInFlight = false;

    const fetchOrganizationUnread = async (organizationId: string) => {
      const unreadByThread = new Map<string, number>();
      const seenCursors = new Set<string>();
      let cursor: string | undefined;

      do {
        const response = await api.get<InboxThreadsPage>('/whatsapp-inbox/threads', {
          params: { organizationId, limit: 100, ...(cursor ? { cursor } : {}) },
        });
        for (const thread of response.data.threads || []) {
          unreadByThread.set(thread.id, Math.max(0, thread.unreadCountAgency || 0));
        }

        const nextCursor = response.data.nextCursor || undefined;
        if (!nextCursor) break;
        if (seenCursors.has(nextCursor)) {
          throw new Error('WhatsApp inbox pagination returned a repeated cursor');
        }
        seenCursors.add(nextCursor);
        cursor = nextCursor;
      } while (cursor);

      return unreadByThread;
    };

    const fetchInboxUnread = async () => {
      if (inboxFetchInFlight) return;
      if (agencyOrganizationIds.length === 0) {
        if (active) setInboxUnreadCount(undefined);
        return;
      }
      inboxFetchInFlight = true;
      try {
        const organizationCounts = await Promise.all(
          agencyOrganizationIds.map(fetchOrganizationUnread)
        );
        if (active) {
          const unreadByThread = new Map<string, number>();
          for (const organizationCount of organizationCounts) {
            for (const [threadId, unreadCount] of organizationCount) {
              unreadByThread.set(threadId, unreadCount);
            }
          }
          inboxUnreadByThreadRef.current = unreadByThread;
          setInboxUnreadCount(
            Array.from(unreadByThread.values()).reduce((total, count) => total + count, 0)
          );
        }
      } catch {
        if (active) {
          inboxUnreadByThreadRef.current = new Map();
          setInboxUnreadCount(undefined);
        }
      } finally {
        inboxFetchInFlight = false;
      }
    };

    const handleUnreadUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{ threadId?: string; unreadCount?: number }>).detail;
      if (!detail?.threadId || typeof detail.unreadCount !== 'number') return;
      const previousCount = inboxUnreadByThreadRef.current.get(detail.threadId);
      if (previousCount === undefined) return;
      const nextCount = Math.max(0, detail.unreadCount);
      if (previousCount === nextCount) return;
      inboxUnreadByThreadRef.current.set(detail.threadId, nextCount);
      setInboxUnreadCount((current) =>
        current === undefined ? current : Math.max(0, current - previousCount + nextCount)
      );
    };

    window.addEventListener(INBOX_UNREAD_UPDATE_EVENT, handleUnreadUpdate);
    void fetchInboxUnread();
    const interval = setInterval(() => {
      fetchUnreadCount();
      void fetchInboxUnread();
    }, 60000);
    return () => {
      active = false;
      clearInterval(interval);
      window.removeEventListener(INBOX_UNREAD_UPDATE_EVENT, handleUnreadUpdate);
    };
  }, [agencyOrganizationIds, fetchUnreadCount, user?.id]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const isOrgAdmin = ['OWNER', 'ADMIN'].includes(user?.roleInOrg || '');

  const [settingsOpen, setSettingsOpen] = useState(() => {
    const settingsPaths = [
      '/agency/team',
      '/agency/brands',
      '/agency/job-templates',
      '/agency/messaging-templates',
      '/agency/contract-templates',
      '/agency/webhooks',
      '/agency/messaging',
      '/agency/google-workspace',
      '/agency/invitations',
      '/agency/messaging-log',
      '/agency/audit',
      '/agency/api-docs',
      '/agency/whatsapp-mock',
      '/agency/whatsapp-canvas',
      '/agency/tags',
      '/agency/kol-data-tools',
      '/agency/extension-devices',
    ];
    return settingsPaths.some((p) => location.pathname.startsWith(p));
  });

  const navGroups: NavGroup[] = useMemo(
    () => [
      {
        label: 'Overview',
        items: [
          { path: '/agency/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
          { path: '/agency/analytics', icon: BarChart3, label: 'Analytics' },
        ],
      },
      {
        label: 'Talent',
        items: [
          { path: '/agency/kols', icon: Users, label: 'KOLs' },
          { path: '/agency/explore', icon: Compass, label: 'Explore' },
          { path: '/agency/invitations', icon: Send, label: 'Invitations' },
          { path: '/agency/kol-performance', icon: BarChart3, label: 'Performance' },
        ],
      },
      {
        label: 'Campaigns',
        items: [
          { path: '/agency/campaigns', icon: Briefcase, label: 'Campaigns' },
          { path: '/agency/submissions', icon: Clapperboard, label: 'Submissions' },
          { path: '/agency/scripts', icon: FileText, label: 'Scripts' },
          { path: '/agency/contracts', icon: ScrollText, label: 'Contracts' },
          { path: '/agency/ai-action-plans', icon: Sparkles, label: 'AI action plans' },
          { path: '/agency/my-assignments', icon: CheckSquare, label: 'My Assignments' },
        ],
      },
      {
        label: 'AI Studio',
        items: [
          { path: '/agency/ai-video-edits', icon: Scissors, label: 'Video & Animation' },
          { path: '/agency/animation-studio', icon: Sparkles, label: 'Animation studio' },
          { path: '/agency/ai-credits', icon: Coins, label: 'AI Credits' },
        ],
      },
      {
        label: 'Operations',
        items: [
          { path: '/agency/inbox', icon: MessageCircle, label: 'WhatsApp Inbox' },
          { path: '/agency/gallery', icon: Images, label: 'Gallery' },
          { path: '/agency/reports', icon: FileDown, label: 'Reports' },
          { path: '/agency/payments-analytics', icon: Wallet, label: 'Payment Analytics' },
        ],
      },
    ],
    []
  );

  const settingsNavItems = [
    { path: '/agency/guide', icon: BookOpen, label: 'Getting started' },
    { path: '/agency/team', icon: UserCog, label: 'Team' },
    { path: '/agency/brands', icon: Building2, label: 'Brands' },
    { path: '/agency/job-templates', icon: Briefcase, label: 'Campaign templates' },
    { path: '/agency/messaging-templates', icon: Mail, label: 'Messaging templates' },
    { path: '/agency/contract-templates', icon: FileText, label: 'Contract Templates' },
    { path: '/agency/tags', icon: Tags, label: 'Tags' },
    { path: '/agency/kol-data-tools', icon: GitMerge, label: 'KOL data tools' },
    { path: '/agency/extension-devices', icon: Chrome, label: 'Extension devices' },
    { path: '/agency/webhooks', icon: Webhook, label: 'Webhooks' },
    { path: '/agency/messaging', icon: MessageCircle, label: 'WhatsApp & messaging' },
    { path: '/agency/whatsapp-canvas', icon: GitMerge, label: 'WA assignment canvas' },
    { path: '/agency/google-workspace', icon: Link2, label: 'Google Workspace' },
    { path: '/agency/messaging-log', icon: ClipboardList, label: 'Messaging log' },
    ...(isOrgAdmin
      ? [
          { path: '/agency/api-docs', icon: Code2, label: 'API docs' },
          { path: '/agency/whatsapp-mock', icon: MessageCircle, label: 'WhatsApp mock lab' },
        ]
      : []),
    { path: '/agency/audit', icon: Shield, label: 'Audit Logs' },
  ];

  const allNavItems = [...flatNavFromGroups(navGroups), ...settingsNavItems];
  const pageTitle =
    location.pathname === '/agency/guide'
      ? 'Getting started'
      : /^\/agency\/scripts\/.+/.test(location.pathname)
        ? 'Script studio'
        : allNavItems.find((item) => location.pathname.startsWith(item.path))?.label || 'Agency';

  const shell = portalShellProps('agency');

  return (
    <div
      className="theme-agency glass-mesh-bg flex h-screen text-glass-text font-sans"
      {...shell}
    >
      <PortalMobileTopBar
        menuOpen={isMobileMenuOpen}
        onToggleMenu={() => setIsMobileMenuOpen((open) => !open)}
        notificationsTo="/agency/notifications"
        unreadCount={unreadCount}
      />

      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 mt-12 bg-[var(--glass-scrim)] backdrop-blur-sm md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div
        className={`
          fixed md:static inset-y-0 left-0 z-50
          w-[264px] flex flex-col
          bg-glass-1 backdrop-blur-[28px] border-r border-glass
          transform transition-transform duration-300 ease-in-out
          md:translate-x-0
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isDesktopSidebarHidden ? 'md:hidden' : ''}
          mt-12 md:mt-0
        `}
      >
        <div className="p-5 border-b border-glass">
          <Link
            to="/"
            className="inline-flex shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50 rounded-md"
          >
            <KolMyGlassLogo variant="nav" />
          </Link>
          <p className="text-xs uppercase tracking-[0.05em] text-glass-text-2 mt-3">Agency Portal</p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto glass-scroll">
          <PortalNavGroups groups={navGroups} scrollWithinSidebar={false} />

          <div className="px-3 pb-2">
          <button
            type="button"
            onClick={() => setSettingsOpen(!settingsOpen)}
            className={`${navLinkBase} ${navLinkInactive} w-full text-left`}
          >
            <Settings className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1">Settings</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${settingsOpen ? 'rotate-180' : ''}`} />
          </button>
          {settingsOpen && (
            <div className="ml-3 mt-1 space-y-0.5 border-l border-glass pl-2">
              {settingsNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname.startsWith(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex min-h-11 items-center gap-3 rounded-[10px] px-3 py-2 text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50 md:min-h-0 ${
                      isActive
                        ? 'bg-[rgb(var(--accent-rgb)/0.14)] text-accentText font-semibold'
                        : navLinkInactive
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          )}
          </div>

          <div className="space-y-1 border-t border-glass p-3">
          <div className="flex items-center gap-2 px-1 pb-2 md:hidden" aria-label="Portal utilities">
            <OpenKanbanButton className="!h-11 !w-11" />
            <SandboxTryoutButton className="!h-11 !w-11" />
            <ThemeToggle className="!h-11 !w-11" />
          </div>
          <button
            type="button"
            onClick={() => setAppearanceOpen((v) => !v)}
            className={`${navLinkBase} ${navLinkInactive} w-full text-left`}
          >
            <Palette className="w-5 h-5" />
            Appearance
          </button>
          {appearanceOpen ? (
            <div className="px-2 pb-2">
              <AppearanceSettings showAgencyAccent compact />
            </div>
          ) : null}
          <Link to="/agency/profile" className={`${navLinkBase} ${navLinkInactive}`}>
            <User className="w-5 h-5" />
            Profile
          </Link>
          <button type="button" onClick={logout} className={`${navLinkBase} ${navLinkInactive} w-full text-left`}>
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </div>
      </div>

      <div className="mt-12 flex flex-1 flex-col overflow-hidden md:mt-0">
        <div className="hidden md:flex h-16 bg-glass-1 backdrop-blur-[28px] border-b border-glass items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsDesktopSidebarHidden(!isDesktopSidebarHidden)}
              className="p-2 text-glass-text-2 hover:text-glass-text hover:bg-glass-3 rounded-lg transition-colors"
              aria-label={isDesktopSidebarHidden ? 'Show sidebar' : 'Hide sidebar'}
              title={isDesktopSidebarHidden ? 'Show sidebar' : 'Hide sidebar'}
            >
              {isDesktopSidebarHidden ? <PanelLeft className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
            </button>
            <h2 className="page-title text-[1.15rem]">{pageTitle}</h2>
          </div>

          <div className="flex items-center gap-3">
            {user && <PortalUserChip user={user} />}
            <OpenKanbanButton />
            <SandboxTryoutButton />
            <ThemeToggle />
            <Link
              to="/agency/notifications"
              className="relative p-2 text-glass-text-2 hover:text-glass-text hover:bg-glass-3 rounded-lg transition-colors"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-accent2 text-white text-[0.6rem] flex items-center justify-center rounded-full shadow-[0_0_10px_rgb(var(--accent-rgb)/0.6)]">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        <div className={`${portalMainClassName(location.pathname)} agency-mobile-tab-clearance`}>
          <div className={portalContentClassName(location.pathname)}>
            <Outlet />
          </div>
        </div>
      </div>

      <PortalBottomTabs
        menuOpen={isMobileMenuOpen}
        onMore={() => setIsMobileMenuOpen((open) => !open)}
        unreadCount={inboxUnreadCount}
      />
      <div data-portal-overlay-root />
    </div>
  );
}
